import type { V2AdminNegotiationDetailDto } from '@idc-forms/api-client'
import { Building2, ExternalLink, ReceiptText, Star } from 'lucide-react'
import { Link } from 'react-router'
import { PATHS, ROUTE_ACTIONS } from '@/app/router/routes'
import { NAME_IDENTIFICATION_CARD } from '@/shared/catalogs/identifications'
import { TypeClient } from '@/shared/catalogs/type-client'
import {
  type EnumTypeFigureProvider,
  NAME_TYPE_FIGURE_PROVIDER
} from '@/shared/catalogs/type-figure-provider'
import { formatCode } from '@/shared/constants/config'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { getFormatOfTypeDocument } from '@/shared/utils/dpi.lib'

/**
 * Un propietario tal como lo publica el detalle v2.
 *
 * La forma cambió respecto de v1: allá el cliente venía APLANADO sobre el propietario
 * —`owner.name`, `owner.code`—, acá vive anidado en `owner.client` y los tres atributos de la
 * RELACIÓN (titular, facturación, figura) quedan afuera. La distinción no es cosmética: el
 * mismo cliente puede ser titular en una negociación y no serlo en otra, y aplanarlo invitaba
 * a confundir un atributo del vínculo con uno de la persona.
 */
type NegotiationOwner = V2AdminNegotiationDetailDto['owners'][number]

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
}

/** Los tres atributos del VÍNCULO, que no son del cliente sino de su papel en el expediente. */
function ClientRoleBadges({ owner }: { owner: NegotiationOwner }) {
  // El contrato publica la figura como TEXTO —el catálogo de etiquetas es de la pantalla, no
  // del contrato—, así que el diccionario se consulta con guarda: una figura histórica que ya
  // no está en el catálogo cae a su propio código en vez de pintar `undefined`.
  const figureLabel = owner.typeFigureProvider
    ? (NAME_TYPE_FIGURE_PROVIDER[owner.typeFigureProvider as EnumTypeFigureProvider] ??
      owner.typeFigureProvider)
    : null

  return (
    <div className="flex flex-wrap items-center gap-1">
      {figureLabel ? (
        <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {figureLabel}
        </span>
      ) : null}
      {owner.default ? (
        <span className="inline-flex items-center gap-1 rounded-sm border border-primary/20 bg-primary/5 px-1.5 py-0.5 text-[10px] font-medium text-primary">
          <Star aria-hidden="true" className="h-2.5 w-2.5" />
          Principal
        </span>
      ) : null}
      {owner.invoice ? (
        <span className="inline-flex items-center gap-1 rounded-sm border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
          <ReceiptText aria-hidden="true" className="h-2.5 w-2.5" />
          Facturación
        </span>
      ) : null}
    </div>
  )
}

export function NegotiationClientsTab({ owners }: { owners: NegotiationOwner[] }) {
  if (owners.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center">
        <p className="text-sm font-medium">Sin clientes enlazados</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Esta negociación no tiene propietarios o gestores asociados.
        </p>
      </div>
    )
  }

  const orderedOwners = [...owners].sort(
    (left, right) => Number(right.default) - Number(left.default)
  )

  return (
    <div className="flex flex-col gap-2">
      {orderedOwners.map((owner) => {
        const client = owner.client
        const identification = client.identification ?? client.nit ?? null
        // La empresa no tiene documento: se identifica por NIT, y v2 publica `null` en el tipo
        // en vez de inventarle uno. La etiqueta «NIT» la pone la pantalla, que es donde vive.
        const identificationType =
          client.identificationType ?? (client.typePerson === TypeClient.JURIDICAL ? 'NIT' : null)
        const identificationLabel = identificationType
          ? identificationType === 'NIT'
            ? 'NIT'
            : (NAME_IDENTIFICATION_CARD[identificationType] ?? identificationType)
          : null
        const identificationNumber =
          identification && identificationType && identificationType !== 'NIT'
            ? getFormatOfTypeDocument({ type: identificationType, number: identification }).format
            : identification
        const personLabel =
          client.typePerson === TypeClient.JURIDICAL ? 'Persona jurídica' : 'Persona individual'

        return (
          <Link
            key={client.id}
            // Sin `clientRouteId`: ese ayudante existe para las respuestas v1, donde el `cli_`
            // podía faltar y había que caer al `_id`. Acá el id público es lo ÚNICO que v2
            // publica, así que no hay a qué caer ni por qué preguntarlo.
            to={PATHS.admin.clients.byType(client.typePerson, ROUTE_ACTIONS.view, client.id)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Abrir cliente ${client.name} en una pestaña nueva`}
            className="group block rounded-lg border p-3 transition-colors hover:border-primary/30 hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <div className="flex min-w-0 items-start gap-3">
              <Avatar seed={client.id} className="h-12 w-12 shrink-0">
                <AvatarImage
                  src={client.photo ?? undefined}
                  alt={client.name}
                  className="object-cover"
                />
                <AvatarFallback className="text-xs uppercase">
                  {initials(client.name) || '?'}
                </AvatarFallback>
              </Avatar>

              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <h4
                      className="min-w-0 flex-1 truncate text-sm font-semibold"
                      title={client.name}
                    >
                      {client.name}
                    </h4>
                    <ExternalLink
                      aria-hidden="true"
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                    />
                  </div>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Building2 aria-hidden="true" className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {formatCode(client.code, 'client')} · {personLabel}
                    </span>
                  </p>
                </div>

                <ClientRoleBadges owner={owner} />

                {identificationNumber || client.email ? (
                  <div className="space-y-0.5 text-xs text-muted-foreground">
                    {identificationNumber ? (
                      <p className="truncate">
                        {identificationLabel ?? 'Identificación'} · {identificationNumber}
                      </p>
                    ) : null}
                    {client.email ? <p className="truncate">{client.email}</p> : null}
                  </div>
                ) : null}
              </div>
            </div>
          </Link>
        )
      })}
    </div>
  )
}
